/**
 * `telegram/TelegramMiniApp.tsx` — the member app inside Telegram, at `/tg`
 * (#2074).
 *
 * Why the member comes: they tapped a roster message's button, or the menu
 * button in the Tarii chat. What they see first: the screen that button
 * named (Calendar by default) — the same phone app as on the web, in their
 * Telegram colours, signed in by Telegram. What they do: whatever they do in
 * the phone app; Telegram's Back button pops stacked screens.
 *
 * The router runs with `basename="/tg"` here (`App.tsx`), so the member
 * routes are the phone app's own (`/me`, `/me/inbox`, …) and every in-app
 * link stays inside the Mini App. `/tg` itself honours `startapp`
 * (`telegram/startApp.ts`). A Telegram user who has not connected a Tarii
 * account gets one button, **Connect this chat** (#2071): it asks the API for
 * a single-use `/connect/telegram?t=<token>` link bound to this chat (signed
 * `initData` is the proof) and opens it in their own browser — signing in
 * there connects the chat by itself. The old three steps stay as a small
 * hint below. Loading and every other problem are one line each (design
 * pass #2071). Every shell state and
 * the app itself clear Telegram's buttons and the home indicator
 * (`useTelegramSafeArea`).
 *
 * The Tarii mark (founder, 12 Oct 2026; design option C): the loading line,
 * every problem line and the Connect screen sit under the full mark, as an
 * app's launch screen does — nothing else is on these screens to crowd.
 */
import { type ReactNode, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { LogoMark } from "../components/Logo";
import { Button, EmptyState } from "../components/ui";
import { useAuth } from "../lib/AuthContext";
import { type MiniAppSessionProblem, requestConnectLink } from "./session";
import { routeForStartParam, stateForStartParam } from "./startApp";
import { openOutsideTelegram, readInitData, readStartParam, telegramWebApp } from "./telegramSdk";
import { useTelegramSafeArea } from "./useTelegramSafeArea";
import { useTelegramTheme } from "./useTelegramTheme";

const PROBLEM_LINES: Record<Exclude<MiniAppSessionProblem, "not_linked">, string> = {
  no_init_data: "Open Tarii from the Tarii chat in Telegram.",
  invalid_init_data: "This link has expired. Close Tarii and open it again from the chat.",
  telegram_off: "Telegram is not turned on for your team in Tarii yet.",
  failed: "Tarii could not open. Close it and try again from the chat.",
};

export function TelegramMiniApp({ children }: { children: ReactNode }) {
  useTelegramTheme();
  useTelegramSafeArea();
  const { loading, accountId, error } = useAuth();

  if (loading) {
    return <OneLine testid="tg-loading">Opening your roster…</OneLine>;
  }
  if (!accountId) {
    if (error === "not_linked") return <UnlinkedScreen />;
    const line =
      error && error in PROBLEM_LINES
        ? PROBLEM_LINES[error as keyof typeof PROBLEM_LINES]
        : PROBLEM_LINES.failed;
    return <OneLine testid="tg-problem">{line}</OneLine>;
  }
  return (
    <Routes>
      <Route index element={<StartAppRedirect />} />
      {children}
      <Route path="*" element={<Navigate to="/me" replace />} />
    </Routes>
  );
}

/** `/tg` → the screen `startapp` names (Calendar when none). */
function StartAppRedirect() {
  const key = readStartParam();
  return <Navigate to={routeForStartParam(key)} replace state={stateForStartParam(key)} />;
}

/** The shell's page: centred, on Telegram's background, clear of its buttons. */
function ShellPage({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-bg px-6 pb-safe-bottom pt-safe-top">
      {children}
    </div>
  );
}

function OneLine({ children, testid }: { children: ReactNode; testid: string }) {
  return (
    <ShellPage>
      <LogoMark label="Tarii" className="mb-5 h-14 w-14" testid="tg-mark" />
      <p className="max-w-xs text-center text-sm text-fg-subtle" data-testid={testid}>
        {children}
      </p>
    </ShellPage>
  );
}

const CONNECT_STEPS = [
  "Sign in to Tarii on the web.",
  "Open your profile — the circle at the top right.",
  "Tap Connect Telegram, then come back here.",
];

type ConnectState = "idle" | "opening" | "opened" | "failed";

/** A Telegram user with no connected Tarii account: one button that opens a
 *  link bound to this chat in their own browser (#2071); the old three steps
 *  as a small hint below. */
export function UnlinkedScreen() {
  const [state, setState] = useState<ConnectState>("idle");
  const connect = async () => {
    setState("opening");
    const url = await requestConnectLink(readInitData(telegramWebApp()));
    if (!url) {
      setState("failed");
      return;
    }
    openOutsideTelegram(url);
    setState("opened");
  };
  return (
    <ShellPage>
      <div className="w-full max-w-sm" data-testid="tg-unlinked">
        {/* The mark stands where the link glyph was (12 Oct 2026). */}
        <LogoMark className="mx-auto mb-4 h-14 w-14" testid="tg-mark" />
        <EmptyState
          title="Connect this chat to Tarii"
          description="Sign in once; this chat connects by itself."
          className="border-0 bg-transparent px-0 py-0"
          action={
            <div className="w-full">
              <Button
                size="lg"
                className="w-full"
                onClick={() => void connect()}
                disabled={state === "opening"}
                data-testid="tg-unlinked-connect"
              >
                {state === "opening" ? "Opening…" : "Connect this chat"}
              </Button>
              {state === "opened" ? (
                <p
                  className="mt-3 text-center text-xs text-fg-muted"
                  data-testid="tg-unlinked-opened"
                >
                  Signed in? Close this and open Tarii again from the chat.
                </p>
              ) : null}
              {state === "failed" ? (
                <p className="mt-3 text-center text-xs text-danger" role="alert">
                  That didn't go through. Close Tarii and open it again from the chat.
                </p>
              ) : null}
              <div className="mt-8 border-t border-border pt-4 text-left">
                <p className="text-xs font-medium text-fg-subtle">Or connect from Tarii itself</p>
                <ol
                  className="mt-2 space-y-1.5 text-xs text-fg-subtle"
                  data-testid="tg-unlinked-steps"
                >
                  {CONNECT_STEPS.map((step, i) => (
                    <li key={step} className="flex items-start gap-2">
                      <span aria-hidden="true" className="w-3 shrink-0 tabular-nums">
                        {i + 1}.
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          }
        />
      </div>
    </ShellPage>
  );
}
